import {
  Component, computed,
  DestroyRef, effect,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {finalize, first, forkJoin, map, Observable, of, Subject,} from 'rxjs';
import {EventType, Router} from '@angular/router';
import {editField, eField} from "../../core/enums/fields.enum";
import {USER_FIELDS} from "../../core/constants/fields";
import {Section} from '../../core/enums/section.enum';
import {IFields , ICodeRequest} from '../../core/interfaces';
import {AuthService} from '../../core/services/auth/auth.service';
import {TimerService} from '../../core/services/timer/timer.service';
import {cleanPhoneNumber} from '../../core/utils';
import {PhoneMaskDirective} from "../../core/directives/phone-input.directive";
import {IFieldApi} from "./interfaces/field-api.interface";
import {HiddenCaptchaComponent} from "../../../assets/shared/hidden-captcha/hidden-captcha.component";
import {EditFieldsFormComponent} from "../../../assets/shared/edit-fields-form/edit-fields-form.component";
import {catchError} from "rxjs/operators";
import QRCodeStyling from "qr-code-styling";
import {AutoFocusDirective} from "../../core/directives/autoFocus-code.directive";
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { RequiredFieldsFormComponent } from '../../../assets/shared/required-fields-form/required-fields-form.component';
import { CookiePopupComponent } from '../../../assets/shared/cookie-popup/cookie-popup.component';
import { PhoneNumberFormatPipe } from '../../core/pipes/phoneHideFormat.pipe';
import { ButtonComponent } from '../../../assets/shared/button/button.component';
import { MatPseudoCheckbox } from '@angular/material/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";


@Component({
  selector: 'login',
  imports: [
    NgxMaskDirective,
    CookiePopupComponent,
    CommonModule,
    ReactiveFormsModule,
    PhoneNumberFormatPipe,
    ButtonComponent,
    RequiredFieldsFormComponent,
    MatPseudoCheckbox, PhoneMaskDirective, HiddenCaptchaComponent, EditFieldsFormComponent, AutoFocusDirective],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild('qrCode') set qrContainer(el: ElementRef | undefined) {
    if (!el) return;
    this.renderQr(el);
  }
  private authService: AuthService = inject(AuthService);
  private timerService = inject(TimerService);
  private router = inject(Router);
  private captchaMode: 'max' | 'standard' | null = null;
  private destroy = inject(DestroyRef)

  public consentChecked: boolean = false;
  public userAgreementChecked: boolean = false;
  public personalDataChecked: boolean = false;
  public sectionENum = Section;
  public phoneForm: FormGroup = new FormGroup({});
  public codeForm: FormGroup = new FormGroup({});
  public isLoading =  signal<boolean>(false);
  public editFields: editField[] = []
  public currentSection = signal<Section>(Section.send);
  public isFocused: boolean = false;
  public phoneErrorMessage: string | null = null;
  public codeErrorMessage: string | null = null;
  public reloadCaptchaTrigger: Subject<void> = new Subject<void>();
  public urlMaxQr: string = '';
  public phoneTouched: boolean = false;
  public phoneFocused:boolean = false;
  public phoneInputStarted = false;
  public timer = this.timerService.timer;
  public timerStatus = this.timerService.timerStatus;
  public fieldsSavedSuccess: boolean = false;
  public errorMessage = signal<string | null>(null);
  public isEditCancelled: boolean = false;
  public isCancelEditText: boolean = false;
  public editFormIsHub: boolean = false;
  public requestBodyAfterCaptchaAndLogin: ICodeRequest | undefined;
  public width = signal(window.innerWidth);
  public adaptiv = computed(() => {
    return this.width() <= 768;
  });
  public arrowFieldState = signal<boolean>(false)

  constructor(private fb: FormBuilder, private zone: NgZone) {
  }

  ngOnInit() {
    this.initForms();
    this.isActualUser()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((isActual) => {
      if (isActual) {
        this.checkOptions();
      }
    });
  }
  ngOnDestroy() {
    this.timerService.stopTimer();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent): void {
    const target = event.target as Window;
    this.width.set(target.innerWidth);
  }



  private checkOptions() {
    if(this.authService.hasEditOption()) {
      this.isEdit();
    }
    else {
      this.noEdit();
    }
  }

  private isActualUser(): Observable<boolean> {
    if (this.authService.isAuthenticated()) {
      return of(true);
    } else {
      return this.authService.refreshToken().pipe(
        map(() => true),
        catchError(() => of(false))
      );
    }
  }

  private isEdit(): void {
    const scopes = this.authService.getCurrentValue().scope.trim().split(/\s+/);
    this.editFields = this.toEditFields(scopes);
    this.isEditCancelled = false;
    this.isCancelEditText = true;
    this.currentSection.set(this.sectionENum.edit);
  }

  private noEdit(): void {
    this.checkConsent();
  }

  public cancelEdit() {
    this.isCancelEditText = false;
    this.isEditCancelled = true;
    this.editFormIsHub = false;
  }

  public canceledCancelEdit() {
    this.isCancelEditText = true;
    this.isEditCancelled = false;
  }

  public editHubIs(check: boolean) {
    this.zone.run(() => {
      this.editFormIsHub = check;
    });
  }

  private initForms() {
    this.phoneForm = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{11,}$/)]],
    });

    this.codeForm = this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(4)]],
    });
  }

  public onPhoneSubmit(again: boolean) {
    if (this.phoneForm.invalid || !this.consentChecked || !this.personalDataChecked || !this.userAgreementChecked) {
      return;
    }


    this.isLoading.set(true);
    this.phoneErrorMessage = null;
    this.reloadCaptchaTrigger.next();
    if (again){
      this.captchaMode = 'standard';
      this.codeForm.get('code')?.setValue('');
    } else {
      this.captchaMode = 'max';
    }
  }

  public goToStandardConfirm(){
    this.captchaMode = 'standard';
    this.reloadCaptchaTrigger.next()
    this.codeForm.reset()
  }

  public onCapchaPassed(token: string) {
    this.requestBodyAfterCaptchaAndLogin = {
      token,
      login: cleanPhoneNumber(this.phoneForm.controls['phoneNumber'].value)
    }
    if (this.captchaMode === 'max') {
      this.getMaxVerificationCode(this.requestBodyAfterCaptchaAndLogin);
    }

    if (this.captchaMode === 'standard') {
      this.getStandardVerificationCode(this.requestBodyAfterCaptchaAndLogin);
    }
  }

  private getMaxVerificationCode(requestBody: ICodeRequest) {
    this.authService.getVerificationCodeMax(requestBody).pipe(finalize(() => this.isLoading.set(false)))
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (response) => {
          this.urlMaxQr = response.body?.data.url || ''
          this.startTimerIfRequired(response);
          this.currentSection.set(Section.confirmMax);
          this.errorMessage.set(null);
        },
        error: (error) => {
          this.startTimerIfRequired(error);
          if (error.status === 503 || error.status === 523) {
            this.getStandardVerificationCode(requestBody)
            this.captchaMode = 'standard'
            this.currentSection.set(Section.confirm);
          } else if (error.status === 400) {
            this.phoneErrorMessage =
              error.error?.errors?.by_field?.login?.[0] ??
              error.error?.errors?.by_field?.token?.[0] ??
              'Ошибка при отправке кода. Попробуйте ещё раз';
          } else if (error.status === 409 || error.status === 422 || error.status === 429) {
            this.phoneErrorMessage =
              error.error?.errors?.common?.[0] ?? 'Ошибка при отправке кода. Попробуйте ещё раз';
          } else {
            this.phoneErrorMessage = 'Ошибка при отправке кода. Попробуйте ещё раз';
          }
        },
      });
  }

  private getStandardVerificationCode(requestBody: ICodeRequest){
    this.authService.getVerificationCode(requestBody)
      .pipe(finalize(() => this.isLoading.set(false)))
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (response) => {
          this.startTimerIfRequired(response);
          this.currentSection.set(Section.confirm);
          this.errorMessage.set(null);
        },
        error: (error) => {
          this.startTimerIfRequired(error);
          if (error.status === 400) {
            this.phoneErrorMessage =
              error.error?.errors?.by_field?.login?.[0] ??
              error.error?.errors?.by_field?.token?.[0] ??
              'Ошибка при отправке кода. Попробуйте ещё раз';
          } else if (error.status === 422 || error.status === 429) {
            this.phoneErrorMessage =
              error.error?.errors?.common?.[0] ?? 'Ошибка при отправке кода. Попробуйте ещё раз';
          } else {
            this.phoneErrorMessage = 'Ошибка при отправке кода. Попробуйте ещё раз';
          }
        },
      });
  }

  public onCodeSubmit(channel: boolean) {
    if (this.codeForm.invalid) {
      return;
    }
    this.isLoading.set(true);
    const rawPhoneNumber = this.phoneForm.get('phoneNumber')?.value;
    const phoneNumber = cleanPhoneNumber(rawPhoneNumber);
    const code = this.codeForm.get('code')?.value;

    this.authService
      .sendVerificationCode(phoneNumber, code,channel)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: () => {
          this.phoneForm.reset();
          this.checkOptions();
          this.codeErrorMessage = null;
          this.errorMessage.set(null);
        },
        error: (error) => {
          if (error['error']?.['error'] == 'invalid_grant') {
            this.errorMessage.set('Неверный код');
          } else {
            this.errorMessage.set('Ошибка при отправке кода. Попробуйте ещё раз');
          }
        },
      });
  }

  public authorize() {
    if (!this.authService.isAuthenticated()) {
      this.currentSection.set(this.sectionENum.auth);
      return;
    }

    this.authService.authorize();
  }

  public goToMax(){
    window.open(this.urlMaxQr, '_blank', 'noopener,noreferrer');
  }

  public goBack() {
    this.timerService.stopTimer();
    this.phoneForm.reset();
    this.reloadCaptchaTrigger.next();
    this.codeErrorMessage = null;
    this.currentSection.set(Section.send);
    this.codeForm.reset();
  }

  public hasFieldError(form: FormGroup, fieldName: string): boolean {
    const control = form.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  private startTimerIfRequired(req: any) {
    let timerValue = parseInt(req.headers.get('Retry-After'));

    if (timerValue) {
      this.timerService.startTimer(timerValue);
    } else {
      this.timerService.stopTimer();
    }
  }

  private checkConsent() {
    forkJoin([
      this.authService.getScopesForConfirm().pipe(first()),
      this.authService.checkIfRequiredConsetsFilled().pipe(first()),
    ]).pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
      next: ([scopesResponse, consentsResponse]) => {
        let confirmConsent = scopesResponse?.data?.scopes?.inconsistent;

        const fields = consentsResponse?.data?.fields as IFieldApi[];
        const fieldConsent = fields?.filter(
          (field: IFieldApi) => field.is_required && field.is_empty
        );

        if (confirmConsent?.length > 0) {
          this.authService.setConsentStatus({confirmConsent});
        }
        if (fieldConsent?.length > 0) {
          const requiredFieldIds = fieldConsent.map((x: any) => x.id);
          let requiredFields = this.mapRequiredFields(requiredFieldIds);
          this.authService.setConsentStatus({fieldConsent: requiredFields});
        }
        this.gotToConsent();
      },
    });
  }

  private gotToConsent() {
    this.fieldsSavedSuccess = false;
    const consent = this.authService.getConsentStatus();
    if (consent.confirmConsent?.length > 0) {
      this.router.navigate(['/confirm']);
    } else if (
      consent.fieldConsent &&
      Object.keys(consent.fieldConsent).length > 0
    ) {
      this.currentSection.set(this.sectionENum.fields);
      this.timerService.stopTimer();
    } else {
      this.authorize();
      this.currentSection.set(this.sectionENum.auth);
    }
  }

  public onPhoneFocus() {
    this.phoneFocused = true;
  }

  public onPhoneBlur() {
    this.phoneFocused = false;
    this.phoneTouched = true;
  }

  public onPhoneInput() {
    this.phoneErrorMessage = null;
    this.phoneInputStarted = true;
  }

  public actualfieldsData() {
    return this.authService.getConsentStatus()?.['fieldConsent'];
  }

  private mapRequiredFields(requiredFieldIds: number[]): IFields {
    const requiredFields: IFields = {};

    requiredFieldIds.forEach((fieldId) => {
      const fieldMapping = this.getFieldMapping(fieldId);
      if (fieldMapping) {
        const {group, field} = fieldMapping;

        if (!requiredFields[group]) {
          requiredFields[group] = {fields: []};
        }

        if ('fields' in USER_FIELDS[group]) {
          USER_FIELDS[group].fields.forEach((f) => {
            if (f.name === field) {
              (requiredFields[group] as { fields: any[] }).fields.push(f);
            }
          });
        }

        if (fieldId === eField.email) {
          USER_FIELDS[group].fields.forEach((f) => {
            if (f.name === 'code') {
              (requiredFields[group] as { fields: any[] }).fields.push(f);
            }
          });
        }
        if (fieldId === eField.password) {
          USER_FIELDS[group].fields.forEach((f) => {
            if (f.name === 'repeatPassword') {
              (requiredFields[group] as { fields: any[] }).fields.push(f);
            }
          });
        }
      }
    });

    return requiredFields;
  }

  private getFieldMapping(
    fieldId: number
  ): { group: string; field: string } | null {
    const fieldMappings: { [key: number]: { group: string; field: string } } = {
      [eField.email]: {group: 'email', field: 'email'},
      [eField.photo]: {group: 'photo', field: 'photo'},
      [eField.fullName]: {group: 'personalInfo', field: 'fullName'},
      [eField.dateBirth]: {group: 'personalInfo', field: 'birth_date'},
      [eField.region]: {group: 'personalInfo', field: 'region'},
      [eField.password]: {group: 'password', field: 'password'},
    };

    return fieldMappings[fieldId] || null;
  }

  private toEditFields(scopes: string[]): editField[] {

    const scopeToEditField: Record<string, editField> = {
      password: editField.password,
      phone: editField.phone,
      email: editField.email,
      photo: editField.photo,
      name: editField.name,
      region: editField.region,
      birthday: editField.birthday,
    };

    return scopes
      .filter(scope => scope in scopeToEditField)
      .map(scope => scopeToEditField[scope]);
  }

  public redirectToClient(): void {
    if (this.authService.getCurrentValue().redirect_uri && this.authService.getCurrentValue().state) {
      window.location.href = `${this.authService.getCurrentValue().redirect_uri}?state=${this.authService.getCurrentValue().state}`;
    } else if (this.authService.getCurrentValue().redirect_uri) {
      window.location.href = this.authService.getCurrentValue().redirect_uri
    }
  }

  private renderQr(el: ElementRef) {
    if (!this.urlMaxQr) return;

    el.nativeElement.innerHTML = '';

    const qr = new QRCodeStyling({
      width: 193,
      height: 193,
      data: this.urlMaxQr,
      image: "assets/images/login/MAX.svg",
      qrOptions: {
        errorCorrectionLevel: "L"
      },
      dotsOptions: {
        color: "#282827",
        type: "extra-rounded"
      },

      backgroundOptions: {
        color: "transparent",
      },

      imageOptions: {
        imageSize: 0.5,
        margin: 0,
        crossOrigin: "anonymous",
      },
    });

    qr.append(el.nativeElement);
  }

  public onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
    }
  }

  public arrowMouseEnter() {
      this.arrowFieldState.set(true)
  }
  public arrowMouseLeave(){
    this.arrowFieldState.set(false)
  }
}
